import React from "react"
import { Slot } from "@radix-ui/react-slot"
import { type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { useScreenReader } from "@/hooks/useScreenReader"
import { buttonVariants } from "./button-variants"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: string
  ariaLabel?: string
  ariaDescribedBy?: string
  ariaPressed?: boolean
  ariaExpanded?: boolean
  ariaHaspopup?: boolean | 'false' | 'true' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog'
  announceOnClick?: string
  announceOnPress?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    asChild = false, 
    loading = false,
    loadingText,
    ariaLabel,
    ariaDescribedBy,
    ariaPressed,
    ariaExpanded,
    ariaHaspopup,
    announceOnClick,
    announceOnPress,
    disabled,
    onClick,
    onKeyDown,
    children,
    ...props 
  }, ref) => {
    const { announceStatus } = useScreenReader()
    
    // Handle click events with announcements
    const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      if (loading || disabled) {
        event.preventDefault()
        return
      }
      
      // Announce click action if specified
      if (announceOnClick) {
        announceStatus(announceOnClick)
      }
      
      // Call original onClick handler
      onClick?.(event)
    }, [loading, disabled, announceOnClick, announceStatus, onClick])
    
    // Handle keyboard events
    const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (loading || disabled) {
        return
      }
      
      // Handle Enter and Space key presses
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        
        // Announce press action if specified
        if (announceOnPress) {
          announceStatus(announceOnPress)
        }
        
        // Trigger click event
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
        })
        event.currentTarget.dispatchEvent(clickEvent)
      }
      
      // Call original onKeyDown handler
      onKeyDown?.(event)
    }, [loading, disabled, announceOnPress, announceStatus, onKeyDown])
    
    // Determine if button should be disabled
    const isDisabled = disabled || loading
    
    // Build ARIA attributes
    const ariaAttributes = {
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-pressed': ariaPressed,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHaspopup,
      'aria-disabled': isDisabled,
      'aria-busy': loading,
    }
    
    // Filter out undefined ARIA attributes
    const filteredAriaAttributes = Object.fromEntries(
      Object.entries(ariaAttributes).filter(([_, value]) => value !== undefined)
    )
    
    // Render loading content
    const renderContent = () => {
      if (loading) {
        return (
          <>
            <Loader2 className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Loading</span>
            {loadingText && <span>{loadingText}</span>}
            {!loadingText && children && <span>{children}</span>}
          </>
        )
      }
      
      return children
    }
    
    const Comp = asChild ? Slot : "button"
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...filteredAriaAttributes}
        {...props}
      >
        {renderContent()}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button }
